import { LitElement, html, nothing } from 'lit';
import { loadOneButton, oneButtonOn } from '../one-button';
import { customElement, property, state } from 'lit/decorators.js';
import { keyed } from 'lit/directives/keyed.js';
import { repeat } from 'lit/directives/repeat.js';
import { api, type FriendRequest } from '../api';
import { currentLang, currentTarget, errorText, num, pairFor, t } from '../i18n';
import { dropShared, hasFreshShared } from '../share-inbox';
import { deleteOwnDrafts, dismissCompleted, dismissCompletedMoment, dismissExpired, draftsView, needsHearing, refreshDrafts, sendDrafts, subscribeDrafts, type CompletedDraft, type DraftsView } from '../drafts';
import { completedState, expiredLine, stateLine, stateOf, tryable, tzOf } from '../drafts-ui';
import { currentOwner, endSession, onSession, session } from '../session';
import type { Allowance, Health, LearnProfile, LearnStatus, MomentListState, MomentSummary, UsageTotals } from '../types';
import { aiLine, aiOf, aiOut, aiReason, observeAllocationChange } from '../ai-allowance';
import { gateOf, renderGate, renderGateCounts } from '../review-gate';
import { go, practicePath } from '../router';
import { feelOn, setFeel } from '../feel';
import { musicChanged, musicOn, setMusic } from '../music';
import { HOME_OVERLAYS } from './home-overlays';
import { diagOn, dropOutboxOf, setDiag } from '../diag';
import { openReport } from './report-sheet';
import { openReminders } from './reminder-sheet';
import { turnOffForSignOut } from '../reminders';
import { renderHomeWays } from './ways-in';
import { openCapture, routeHere, savedRecordingPath } from './capture-open';
import './capture-sheet';
import { openMoments } from './moments-sheet';
import { closeHash, focusBack, hashOpen, openHash } from './home-hash';
import { tPart } from '../bidi-html';
import { townPropsOf } from '../town/props';
import { ackTownSeen, homeOpens, markShown, releaseFirstNight } from '../first-night';
import { markOpened, nextLanding } from '../town/landing';
import { normaliseTownName } from '../town-name.generated';
import { loadOpening } from './opening-view';
import { restingCaption, type OwnTown } from '../town/own-town';
import type { HomeTown } from '../town/home-town';
import type { EavesMap, MapDoneDetail } from '../opening';
import './invite-sheet';
import './moments-sheet';
import './newcomers-offer';
import './raid/raid-gate';
import './activity-indicator';
import './friend-request-gate';
import { raidApi, type RaidOverview } from './raid/raid-api';
import { subscribeLive } from '../live';
import { raidWaits } from './raid/raid-army';
import { REST } from './moment-view';
import { DAY_MIN } from '../town/sky';
import { mountResearchControls, type ResearchControlCopy } from '../research/research-controls';
import { mountReplayControls, type ReplayControlCopy } from '../research/replay-controls';
import { replay, research } from '../research-client';

const PAGE = 20;
/** Friends (the circle, and raids since v1 ticket 47): home's way in, in beta. */
const SHOW_CIRCLE = true;
/** The legend sheet's history entry on home. */
const LEGEND = '#legend';

/** The moments pill's icon: a short list. */
const LIST = html`<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M8 6h12M8 12h12M8 18h12M4 6h.01M4 12h.01M4 18h.01"/></svg>`;
/** The town is read with word lookups, so the legend credits them. */
const hasCredit = (own: OwnTown | null): boolean => !!own?.lexicon && Object.keys(own.lexicon).length > 0;

@customElement('home-view')
export class HomeView extends LitElement {
  /** `/moment/<id>`: that moment's panel is up over the town (moment-view.ts); '' on home itself. */
  @property({ attribute: false }) momentId = '';
  /** The moment whose panel is on screen: it outlives `momentId` by the slide down. */
  @state() private panelId = '';
  @state() private panelLeaving = false;
  /** The panel was opened from home itself (a history entry on top of it), so closing it goes back. */
  private panelPushed = false;
  private leaveTimer = 0;
  /** Something on the panel changed what the town draws: it is read again as the panel closes. */
  private panelChanged = false;
  @state() private moments: MomentSummary[] = [];
  @state() private total = 0;
  @state() private filter: MomentListState = 'all';
  @state() private newCount = 0;
  @state() private practicedCount = 0;
  @state() private health?: Health;
  /** Raids (v1 ticket 47), beta only: an army at the gates or an unread report; a failed read shows nothing. */
  @state() private raids?: RaidOverview;
  /** Friend requests waiting for an answer (beta): the line above Record and Type. */
  @state() private requests: FriendRequest[] = [];
  @state() private usage?: UsageTotals;
  @state() private learn?: LearnStatus;
  @state() private profile?: LearnProfile;
  /** Beta: new moments left today, shown next to the create buttons. */
  @state() private allowance?: Allowance;
  @state() private allocationChange = '';
  private allocationOwner: string | null = null;
  private allowanceRead = 0;
  @state() private loading = true;
  @state() private loadingMore = false;
  @state() private error = '';
  @state() private query = '';
  /** The signed-in learner's drafts on this device (ticket 12). */
  @state() private drafts: DraftsView = draftsView();
  @state() private retrying = false;
  @state() private draftsMsg = '';
  /** Signing out with drafts on this device: the keep/delete choice is open. */
  @state() private leaving = false;
  @state() private signingOut = false;
  /** The town's code has loaded (it loads once home has read the town). */
  @state() private townReady = false;
  /**
   * The learner's town as home read it (v1 ticket 40): always read, so a learner with no moment sees the empty land.
   * Home works while it is read, as before (a slow town never holds back a draft or the ways in); the town and the
   * empty state wait for it, since it may open the opening or a landing instead.
   */
  @state() private own: OwnTown | null = null;
  /** The town read has settled (read, or failed and home goes on without it). */
  @state() private townSettled = false;
  /** The naming sheet (the map in ask mode) over the town: due once for a learner from before, or from the ⋯ menu. */
  @state() private asking: 'due' | 'menu' | null = null;
  /** "Watch the opening" from the ⋯ menu plays over home, and home is there when it ends. */
  @state() private replaying = false;
  /** The naming sheet is due, once the town's first frame is on screen. */
  private namingDue = false;
  private townGen = 0;
  /** "Sounds and vibration" in the ⋯ menu (ticket 37): this device's switch for the round's effects. */
  @state() private feel = feelOn();
  @state() private music = musicOn();
  /** "Send error reports" in the ⋯ menu (ticket 43): this device's field signals, `li-diag`. */
  @state() private diag = diagOn();
  /** Beta: this learner may invite testers (OWNER_USER_IDS), and the invite sheet is open. */
  @state() private owner = false;
  @state() private inviting = false;
  /** The legend sheet ("What am I seeing?") is open over the town: a history entry on home (`/#legend`). */
  @state() private legendOpen = hashOpen(LEGEND);
  private dockObserver?: ResizeObserver;
  /** The floating dock's notes chip, opened in place (one capture button only). */
  @state() private notesOpen = false;
  private unsubDrafts?: () => void;
  private unsubLive?: () => void;
  /** The pair (and so whether a starting check exists) settles with the session. */
  private unsubSession?: () => void;

  private debounce?: number;
  private observer?: IntersectionObserver;
  /** Guards against out-of-order responses: only the latest query may write state. */
  private requestGen = 0;

  protected createRenderRoot() { return this; }

  async connectedCallback() {
    super.connectedCallback();
    void loadOneButton().then((on) => on && this.requestUpdate());
    this.unsubDrafts = subscribeDrafts((v) => {
      this.drafts = v;
    });
    this.unsubSession = onSession((s) => {
      this.syncAllocationOwner();
      this.requestUpdate();
      if (s.status === 'known' && !s.provisional) void this.loadAllowance();
    });
    window.addEventListener('focus', this.onAllowanceRefresh);
    document.addEventListener('visibilitychange', this.onAllowanceRefresh);
    window.addEventListener('drafts-sent', this.onDraftsSent);
    window.addEventListener('keydown', this.onKey);
    window.addEventListener('popstate', this.onLegendPop);
    window.addEventListener('resize', this.onResize);
    document.documentElement.classList.add('home-open');
    // Opened straight at `/#report`: the report sheet over the town.
    if (hashOpen('#report')) openReport();
    if (hashOpen('#reminders')) openReminders();
    void this.readTown();
    try {
      const [health, usage, learn] = await Promise.all([
        api.health(),
        api.usage().catch(() => undefined),
        api.learnStatus().catch(() => undefined),
        this.loadAllowance(),
      ]);
      this.health = health;
      if (health.beta) api.owner().then((r) => (this.owner = r.owner)).catch(() => {});
      // The gate line and the Friends mark follow the raids as they happen (live.ts), not only as home opened.
      if (health.beta && this.isConnected) {
        this.unsubLive = subscribeLive(() => {
          void api.friends().then((f) => (this.requests = f.incoming)).catch(() => {});
          return raidApi.overview().then((r) => (this.raids = r));
        });
      }
      this.usage = usage;
      this.learn = learn;
      api.learnProfile().then((p) => (this.profile = p)).catch(() => {});
      await this.loadPage(true);
    } catch (e) {
      this.error = errorText(e);
    } finally {
      this.loading = false;
    }
  }

  disconnectedCallback() {
    this.researchCleanup?.();
    this.researchCleanup = undefined;
    this.researchContainer = null;
    this.replayCleanup?.();
    this.replayCleanup = undefined;
    this.replayContainer = null;
    super.disconnectedCallback();
    this.observer?.disconnect();
    if (this.debounce) window.clearTimeout(this.debounce);
    this.unsubDrafts?.();
    this.unsubSession?.();
    this.unsubLive?.();
    this.unsubLive = undefined;
    window.removeEventListener('focus', this.onAllowanceRefresh);
    document.removeEventListener('visibilitychange', this.onAllowanceRefresh);
    window.removeEventListener('drafts-sent', this.onDraftsSent);
    window.removeEventListener('keydown', this.onKey);
    window.removeEventListener('popstate', this.onLegendPop);
    window.removeEventListener('resize', this.onResize);
    this.dockObserver?.disconnect();
    this.closeNotesListeners();
    this.overlayObserver?.disconnect();
    this.overlayObserver = undefined;
    this.syncInert(true);
    window.clearTimeout(this.leaveTimer);
    for (const c of ['home-open', 'invite-open', 'legend-open']) document.documentElement.classList.remove(c);
  }

  /** New moments left today. Reloaded whenever the moment list is, so an upload that
   *  spent a slot elsewhere (a draft, a retry) cannot leave a stale count on screen. */
  private async loadAllowance() {
    const read = ++this.allowanceRead;
    const owner = currentOwner();
    this.syncAllocationOwner();
    const allowance = await api.allowance().catch(() => undefined);
    if (read !== this.allowanceRead || owner !== currentOwner()) return;
    if (allowance) {
      const nextAi = aiOf(allowance);
      const previousMonth = aiOf(this.allowance)?.month;
      const change = observeAllocationChange(nextAi);
      if (previousMonth && nextAi?.month !== previousMonth) this.allocationChange = '';
      if (change) this.allocationChange = change;
    }
    this.allowance = allowance;
  }

  private syncAllocationOwner() {
    const owner = currentOwner();
    if (owner === this.allocationOwner) return;
    this.allocationOwner = owner;
    this.allocationChange = '';
  }

  private onAllowanceRefresh = () => {
    if (document.visibilityState !== 'hidden') void this.loadAllowance();
  };

  /** A draft became a lesson — refresh the list so it appears (and, on the empty land, land it). */
  private onDraftsSent = () => {
    void this.loadPage(true);
    void this.loadAllowance();
    // With the capture panel up, the landing waits for it to close (onCaptureClose).
    if (document.documentElement.classList.contains('capture-open')) this.landAfterCapture = true;
    else if (this.own && !this.own.districts.length) void this.readTown(true);
  };

  private landAfterCapture = false;
  private onCaptureClose = () => {
    if (this.namingDue && this.townSettled) window.setTimeout(this.onTownShown, 300);
    if (!this.landAfterCapture) return;
    this.landAfterCapture = false;
    if (this.own && !this.own.districts.length) void this.readTown(true);
  };

  /**
   * Reads the town and opens what it calls for (v1 ticket 40): the opening, a moment waiting to land (41), else the
   * town (or the empty land), with the naming sheet over it when due. `again` (a draft landed on the empty land) only
   * opens a landing; the town on screen stays. Opened on a shared voice message (`/#shared`), a landing waits for a
   * later visit; settled on plain home with one waiting (after the opening or a landing), its sheet opens.
   */
  private async readTown(again = false) {
    const gen = ++this.townGen;
    let own: OwnTown;
    try {
      own = await api.town();
    } catch {
      if (gen !== this.townGen || again) return;
      // The town is extra: home works without it, and whatever the first night held goes.
      this.townSettled = true;
      void releaseFirstNight();
      void this.offerShared();
      return;
    }
    if (gen !== this.townGen || !this.isConnected) return;
    const opens = homeOpens(own);
    if (opens === 'opening' && !again) {
      go('/opening', true);
      return;
    }
    if (opens === 'landing' && !this.momentId && routeHere()?.tab !== 'shared') {
      const id = nextLanding(own)!;
      markOpened(id);
      // A draft that became this moment has its notice on home; the landing shows the moment, so it goes.
      dismissCompletedMoment(id);
      go(`/land/${id}`);
      return;
    }
    if (again) return;
    this.namingDue = opens === 'naming';
    this.own = own;
    this.townSettled = true;
    this.loadTown();
    void this.offerShared();
  }

  /** Home settled on itself (no panel, no moment) with a shared voice message waiting: its sheet opens over home. */
  private async offerShared() {
    const plain = () => this.isConnected && location.pathname === '/' && !routeHere() && !this.momentId;
    if (plain() && (await hasFreshShared()) && plain()) openCapture('shared');
  }

  /** The town's first frame is on screen: the first night's layers go, and the naming sheet comes if it is due. */
  private onTownShown = () => {
    void releaseFirstNight();
    // A moment's panel or the capture panel is up (a deep link, a shared message): the naming sheet waits until it closes.
    // Home already left (the late call after a panel closed): the sheet stays due for the next visit.
    if (!this.namingDue || this.momentId || routeHere() || !this.isConnected) return;
    this.namingDue = false;
    markShown('naming');
    void this.openAsk('due');
  };

  /** The naming sheet over the town, the map's ask mode, from the opening chunk. */
  private async openAsk(why: 'due' | 'menu') {
    this.closeMenu();
    try {
      await loadOpening();
    } catch {
      return;
    }
    if (this.isConnected) this.asking = why;
  }

  /** Laid (named, or renamed) or Not now: the sheet fades, then the stone rises by the signpost with the build sound. */
  private onMapDone = async (e: Event) => {
    const map = e.currentTarget as EavesMap, d = (e as CustomEvent<MapDoneDetail>).detail;
    if (!d.laid && this.asking === 'due') void ackTownSeen('naming');
    await map.lift();
    this.asking = null;
    if (!d.laid || !this.own) return;
    this.own = { ...this.own, ...(d.name ? { name: d.name } : {}), ...(d.foundedAt ? { foundedAt: d.foundedAt } : {}), stone: true };
    const town = this.querySelector<HomeTown>('home-town');
    await town?.raiseStone({ name: d.name, foundedAt: d.foundedAt ?? undefined, stone: true });
  };

  /** "Watch the opening": a replay over home, with no naming after it. */
  private watchOpening = async () => {
    this.closeMenu();
    try {
      await loadOpening();
    } catch {
      return;
    }
    if (!this.isConnected) return;
    // The replay's example draws its own town: home's is freed meanwhile (one WebGL context), as tending does.
    this.querySelector<HomeTown>('home-town')?.pause?.();
    this.replaying = true;
  };

  private onReplayEnd = () => {
    this.replaying = false;
    void this.querySelector<HomeTown>('home-town')?.resume?.();
  };

  private closeMenu() {
    this.querySelector<HTMLDetailsElement>('details.overflow-menu')?.removeAttribute('open');
  }

  /**
   * The notice's "Send all now": the same authoritative read the automatic
   * path uses — each draft is asked about by its own id first, and the server
   * decides. Nothing leaves this device on a refusal, and a lesson that was
   * already made is opened rather than made twice.
   */
  private async retryDrafts() {
    this.retrying = true;
    this.draftsMsg = '';
    try {
      const r = await sendDrafts({ force: true });
      const made = r.completed.filter((c) => c.momentId).length;
      const reconciled = r.reconciledIds.length;
      const newLessons = made - reconciled;
      if (reconciled > 0 && newLessons > 0) {
        this.draftsMsg = `${newLessons === 1 ? t('home.uploadedOne') : t('home.uploadedMany', { n: newLessons })}${t('common.dot')}${reconciled === 1 ? t('home.reconciledOne') : t('home.reconciledMany', { n: reconciled })}`;
      } else if (made > 0 && reconciled === made) this.draftsMsg = reconciled === 1 ? t('home.reconciledOne') : t('home.reconciledMany', { n: reconciled });
      else if (made > 0) this.draftsMsg = made === 1 ? t('home.uploadedOne') : t('home.uploadedMany', { n: made });
      else if (r.offline || r.skipped) this.draftsMsg = t('home.stillOffline');
      else if (r.blocked) this.draftsMsg = t('drafts.stillWaiting');
      // A refusal may have fixed today's review batch — show it.
      void this.loadAllowance();
    } finally {
      this.retrying = false;
    }
  }

  private openCompleted(c: CompletedDraft) {
    if (!c.momentId) return;
    dismissCompleted(c.id);
    // A draft that became a moment lands in the town like any new moment (v1 ticket 41).
    go(`/land/${c.momentId}`);
  }

  private resumeRecording = () => {
    const draft = this.drafts.drafts.find(needsHearing);
    if (draft) go(savedRecordingPath(draft.id));
  };

  /**
   * The signed-in learner's drafts, in short: how many and why they wait,
   * lessons they just became, and the way to manage them. Nobody else's.
   */
  private renderDraftsNotice() {
    const v = this.drafts;
    if (v.readError) return html`
      <div class="card drafts-notice" role="alert" style="margin-top:8px">
        <div class="error small">${t('drafts.readError')}</div>
        <button class="btn drafts-read-retry" style="margin-top:6px" @click=${() => void refreshDrafts()}>${t('common.retry')}</button>
      </div>
    `;
    if (!v.owner || (v.drafts.length === 0 && v.completed.length === 0 && !v.expired)) return nothing;
    const count = (s: string) => v.drafts.filter((d) => d.state === s).length;
    const local = v.drafts.filter((d) => d.state === 'local');
    const counts = [
      local.filter((d) => !d.ambiguous).length ? t('drafts.countPending', { n: local.filter((d) => !d.ambiguous).length }) : '',
      local.filter((d) => d.ambiguous).length ? t('drafts.countUnclear', { n: local.filter((d) => d.ambiguous).length }) : '',
      count('blocked') ? t('drafts.countBlocked', { n: count('blocked') }) : '',
      count('failed') ? t('drafts.countFailed', { n: count('failed') }) : '',
    ].filter(Boolean).join(t('common.dot'));
    const blocked = v.drafts.find((d) => d.state === 'blocked');
    return html`
      <div class="card drafts-notice" role="status" style="margin-top:8px">
        ${v.drafts.length
          ? html`
            <div><strong>${t('drafts.title', { n: v.drafts.length, max: v.max })}</strong></div>
            ${counts ? html`<div class="muted small drafts-counts">${counts}</div>` : nothing}
            ${v.sendingId ? html`<div class="muted small drafts-sending"><span class="spinner"></span> ${t('record.sending')}</div>` : nothing}
            ${blocked ? html`<div class="muted small drafts-blocked">${stateLine(blocked, v.sendingId === blocked.id, tzOf(this.allowance))}</div>` : nothing}
            ${this.draftsMsg ? html`<div class="muted small drafts-msg">${this.draftsMsg}</div>` : nothing}`
          : nothing}
        ${v.expired
          ? html`<div class="drafts-expired" style="display:flex;gap:8px;align-items:center;padding-top:6px">
              <div class="small" style="flex:1;min-width:0">${expiredLine(v.expired)}</div>
              <button class="btn ghost draft-dismiss" aria-label=${t('drafts.dismiss')} @click=${dismissExpired}>✕</button>
            </div>`
          : nothing}
        ${v.completed.map(
          (c) => html`
            <div class="draft" data-state=${completedState(c)} data-id=${c.id}
              style="display:flex;gap:8px;align-items:center;padding-top:6px">
              <div class="small draft-state" style="flex:1;min-width:0">
                ${c.kind === 'audio' ? t('record.pendingAudio') : t('record.pendingText')} — ${c.removed ? t('drafts.removed') : t('drafts.completed')}
              </div>
              ${c.momentId ? html`<button class="btn draft-open" @click=${() => this.openCompleted(c)}>${t('drafts.open')}</button>` : nothing}
              <button class="btn ghost draft-dismiss" aria-label=${t('drafts.dismiss')} @click=${() => dismissCompleted(c.id)}>✕</button>
            </div>
          `,
        )}
        ${v.drafts.some(needsHearing) ? html`<p class="muted small">${t('drafts.recordingKept')}</p>
          <button class="btn drafts-resume-recording" @click=${this.resumeRecording}>${t('drafts.resumeRecording')}</button>` : nothing}
        ${v.drafts.length
          ? html`<div style="display:flex;gap:8px;margin-top:6px">
              ${v.drafts.some((d) => !needsHearing(d) && tryable(stateOf(d, v)))
                ? html`<button class="btn drafts-try" ?disabled=${this.retrying || !!v.sendingId} @click=${() => this.retryDrafts()}>
                    ${this.retrying ? html`<span class="spinner"></span>` : t('drafts.tryAll')}
                  </button>`
                : nothing}
              <button class="btn ghost drafts-manage" style="flex:1" @click=${() => go(oneButtonOn() ? '/#type' : '/record')}>${t('drafts.manage')}</button>
            </div>`
          : nothing}
      </div>
    `;
  }

  /** Signing out with drafts here: delete them, keep them (hidden) for this account, or stay. */
  private renderLogoutChoice() {
    if (!this.leaving) return nothing;
    if (this.drafts.readError) return html`
      <div class="card logout-drafts" role="alertdialog" aria-labelledby="logout-drafts-title" style="margin-bottom:10px">
        <div id="logout-drafts-title"><strong>${t('drafts.readError')}</strong></div>
        <button class="btn logout-drafts-retry" style="margin-top:8px" @click=${() => void refreshDrafts()}>${t('common.retry')}</button>
        <button class="btn ghost block logout-cancel" style="margin-top:8px" @click=${() => (this.leaving = false)}>${t('drafts.logoutCancel')}</button>
      </div>
    `;
    const n = this.drafts.drafts.length;
    return html`
      <div class="card logout-drafts" role="alertdialog" aria-labelledby="logout-drafts-title" style="margin-bottom:10px">
        <div id="logout-drafts-title"><strong>${n === 1 ? t('drafts.logoutOne') : t('drafts.logoutMany', { n })}</strong></div>
        <div class="muted small" style="margin-top:4px">${t('drafts.logoutBody')}</div>
        <button class="btn danger block logout-delete" style="margin-top:8px" ?disabled=${this.signingOut} @click=${() => this.signOut(true)}>
          ${t('drafts.logoutDelete')}
        </button>
        <button class="btn block logout-keep" style="margin-top:8px" ?disabled=${this.signingOut} @click=${() => this.signOut(false)}>
          ${t('drafts.logoutKeep')}
        </button>
        <button class="btn ghost block logout-cancel" style="margin-top:8px" ?disabled=${this.signingOut} @click=${() => (this.leaving = false)}>
          ${t('drafts.logoutCancel')}
        </button>
      </div>
    `;
  }

  /** The town is its own chunk, fetched once home has read the town. */
  private loadTown() {
    if (this.townReady) return;
    import('../town/home-town').then(() => { this.townReady = true; }, () => { void releaseFirstNight(); });
  }

  /** One request per page — practice state already inline, no N+1 progress fetches. */
  private async loadPage(reset: boolean) {
    const gen = ++this.requestGen;
    const filter = this.filter;
    if (reset) {
      this.moments = [];
      this.total = 0;
    } else {
      this.loadingMore = true;
    }
    try {
      const page = await api.listMoments({
        limit: PAGE,
        offset: reset ? 0 : this.moments.length,
        q: this.query,
        state: filter,
      });
      // A newer query started while this fetch was in flight — discard it.
      if (gen !== this.requestGen) return;
      this.total = page.total;
      this.newCount = page.newCount ?? 0;
      this.practicedCount = page.practicedCount ?? 0;
      this.moments = reset ? page.moments : [...this.moments, ...page.moments];
    } catch (e) {
      if (gen !== this.requestGen) return;
      if (reset) this.error = errorText(e);
    } finally {
      if (gen === this.requestGen) this.loadingMore = false;
    }
  }

  private setFilter(f: MomentListState) {
    if (this.filter === f) return;
    this.filter = f;
    void this.loadPage(true);
  }

  private onQuery = (e: InputEvent) => {
    this.query = (e.target as HTMLInputElement).value;
    if (this.debounce) window.clearTimeout(this.debounce);
    this.debounce = window.setTimeout(() => { void this.loadPage(true); }, 300);
  };

  /** Infinite scroll: when the sentinel enters view, fetch the next page. */
  protected updated() {
    const researchContainer = this.querySelector<HTMLElement>('.research-settings');
    if (researchContainer && (researchContainer !== this.researchContainer || currentLang() !== this.researchLanguage)) {
      this.researchCleanup?.();
      this.researchContainer = researchContainer;
      this.researchLanguage = currentLang();
      const copy: ResearchControlCopy = {
        title: t('research.title'), explanation: t('research.explanation'), explanationDefault: t('research.explanationDefault'), neverCollected: t('research.neverCollected'),
        agree: t('research.agree'), join: t('research.join'), stop: t('research.stop'),
        joining: t('research.joining'), stopping: t('research.stopping'), stopped: t('research.stopped'),
        unavailable: t('research.unavailable'), statusActive: t('research.statusActive'),
        generationAgree: t('research.generationAgree'), generationEnable: t('research.generationEnable'),
        generationDisable: t('research.generationDisable'), generationActive: t('research.generationActive'),
        generationUnavailable: t('research.generationUnavailable'),
      };
      this.researchCleanup = mountResearchControls(researchContainer, research, copy);
    }
    const replayContainer = this.querySelector<HTMLElement>('.replay-settings');
    if (replayContainer && (replayContainer !== this.replayContainer || currentLang() !== this.replayLanguage)) {
      this.replayCleanup?.();
      this.replayContainer = replayContainer;
      this.replayLanguage = currentLang();
      const replayCopy: ReplayControlCopy = {
        title: t('replay.title'), explanation: t('replay.explanation'), retention: t('replay.retention'), retentionOne: t('replay.retentionOne'),
        agree: t('replay.agree'),
        start: t('replay.start'), stop: t('replay.stop'), stopAndClear: t('replay.stopAndClear'), reset: t('replay.reset'),
        starting: t('replay.starting'), stopping: t('replay.stopping'), resetting: t('replay.resetting'),
        unavailable: t('replay.unavailable'), inactive: t('replay.inactive'), active: t('replay.active'),
        stopped: t('replay.stopped'), cleared: t('replay.cleared'), researchRequired: t('replay.researchRequired'), notActivated: t('replay.notActivated'),
      };
      this.replayCleanup = mountReplayControls(replayContainer, research, replay, replayCopy);
    }
    const root = document.documentElement;
    root.classList.toggle('invite-open', this.inviting || this.leaving);
    root.classList.toggle('legend-open', this.legendOpen && !!this.own);
    this.watchDock();
    this.watchOverlays();
    // Once per panel and per town element (the town mounts after home's first render): later updates leave the camera
    // where the learner may have moved it.
    const town = this.querySelector('home-town');
    if (this.momentId && town && (this.focusedId !== this.momentId || this.focusedTown !== town)) this.focusPanel();
    // The list renders inside the moments sheet, which renders after this element.
    void this.querySelector<LitElement>('moments-sheet')?.updateComplete.then(() => this.observeList());
  }

  private overlayObserver?: MutationObserver;
  private researchContainer: HTMLElement | null = null;
  private researchLanguage = '';
  private researchCleanup?: () => void;
  private replayContainer: HTMLElement | null = null;
  private replayLanguage = '';
  private replayCleanup?: () => void;

  /** While any overlay is open, the bar and the dock behind it are `inert`, so the keyboard stays in the sheet. */
  private watchOverlays() {
    if (!this.overlayObserver) {
      this.overlayObserver = new MutationObserver(() => this.syncInert());
      this.overlayObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    }
    this.syncInert();
  }

  private observedBar: Element | null = null;
  private observedDock: Element | null = null;
  /** The town element last given the band: one that mounts after the bar and panel were measured needs it too. */
  private bandTown: Element | null = null;

  private syncInert(off = false) {
    const cls = document.documentElement.classList;
    const covered = !off && HOME_OVERLAYS.some((c) => cls.contains(c));
    for (const el of this.querySelectorAll<HTMLElement>('.home-bar, .home-dock')) el.inert = covered;
  }

  /** The idle camera frames the town between the top bar and the panel: they are measured whenever they resize. */
  private watchDock() {
    const bar = this.querySelector('.home-bar'), dock = this.querySelector('.home-dock');
    if (!bar || !dock || typeof ResizeObserver === 'undefined') return;
    const town = this.querySelector('home-town');
    if (town && town !== this.bandTown) {
      this.bandTown = town;
      this.onResize();
    }
    if (this.dockObserver && this.observedDock === dock && this.observedBar === bar) return;
    this.dockObserver?.disconnect();
    this.dockObserver = new ResizeObserver(() => this.onResize());
    this.dockObserver.observe(bar);
    this.dockObserver.observe(dock);
    this.observedBar = bar;
    this.observedDock = dock;
  }

  /** After the renderer's own resize (two frames), the town is framed again between the bar and the panel. */
  private onResize = () => {
    requestAnimationFrame(() => requestAnimationFrame(() => this.reframe()));
  };

  private reframe() {
    const town = this.querySelector<HomeTown>('home-town');
    const bar = this.querySelector('.home-bar'), dock = this.querySelector('.home-dock');
    if (!town?.reframe || !bar || !dock) return;
    const top = bar.getBoundingClientRect().bottom, d = dock.getBoundingClientRect();
    // Hidden under an overlay, the dock measures nothing: the idle band then runs to the foot of the screen.
    town.reframe(top, d.height > 0 ? d.top : innerHeight);
    this.focusPanel();
  }

  private observeList() {
    this.observer?.disconnect();
    const sentinel = this.querySelector('#list-sentinel');
    if (!sentinel || this.moments.length >= this.total) return;
    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((en) => en.isIntersecting) && !this.loadingMore && !this.loading) {
          void this.loadPage(false);
        }
      },
      { rootMargin: '400px' },
    );
    this.observer.observe(sentinel);
  }

  private isNew(m: MomentSummary): boolean {
    return (m.savedActivityTasks ?? m.testedTasks ?? 0) === 0;
  }

  /** The ⋯ menu's "Sounds and vibration" switch (v1 ticket 37), for this device. */
  private toggleFeel = () => {
    setFeel(!this.feel);
    this.feel = feelOn();
    musicChanged();
  };

  /** The ⋯ menu's "Town music" switch, for this device. */
  private toggleMusic = () => {
    setMusic(!this.music);
    this.music = musicOn();
  };

  /** The ⋯ menu's "Send error reports" switch (v1 ticket 43), for this device. */
  private toggleDiag = () => {
    setDiag(!this.diag);
    this.diag = diagOn();
  };

  /** Beta accounts: end this device's session, then show sign-in — asking first about any drafts. */
  private logout = async () => {
    this.closeMenu();
    // The subscription may still hold its initial empty snapshot. Logout must
    // decide from an IndexedDB read that has completed for this account.
    const drafts = await refreshDrafts();
    if (drafts.readError) {
      this.leaving = true;
      return;
    }
    if (drafts.owner && drafts.drafts.length > 0) {
      this.leaving = true;
      return;
    }
    await this.signOut(false);
  };

  /** Kept drafts stay on this device, invisible and unsent, until the same account signs in again. */
  private async signOut(deleteDrafts: boolean) {
    this.signingOut = true;
    try {
      // A shared voice message waiting on this device is another person's voice: it goes with the session.
      await dropShared();
      if (deleteDrafts) {
        await deleteOwnDrafts();
        // Their waiting problem reports go with the drafts.
        const owner = currentOwner();
        if (owner) dropOutboxOf(owner);
      }
      // This phone's reminders go off first, while the session can still delete its device row.
      await turnOffForSignOut();
      try {
        await api.signout();
      } catch { /* already signed out server-side — still leave */ }
      endSession();
      this.leaving = false;
      go('/signin', true);
    } finally {
      this.signingOut = false;
    }
  }

  private renderMomentItem(m: MomentSummary, fresh: boolean) {
    return html`
      <div class="moment-route">
        <button class="moment-item ${fresh ? 'is-new' : ''}" @click=${() => go(`/moment/${m.id}`)}>
          <div class="title" dir="auto">${m.title}</div>
          <div class="muted small" style="margin-bottom:6px" dir="auto">${m.situation.slice(0, 120)}${m.situation.length > 120 ? '…' : ''}</div>
          <div class="meta">
            <span>${fmtDate(m.createdAt)}</span>
            ${m.newcomers
              ? html`<span class="pill newcomers-pill">${t('newcomers.pill')}</span>`
              : m.source === 'text' ? html`<span class="pill">${t('home.wantedPill')}</span>` : nothing}
            ${m.imagined ? html`<span class="pill imagined-pill">${t('land.imagined')}</span>` : nothing}
            ${m.shared ? html`<span class="pill shared-pill">${t('shared.pill')}</span>` : nothing}
            ${m.durationSec ? html`<span>${t('record.seconds', { n: Math.round(m.durationSec) })}</span>` : nothing}
            <span>${t(m.vocabCount === 1 ? 'home.itemsOne' : 'home.items', { n: m.vocabCount })}</span>
            ${fresh
              ? html`<span class="pill accent">${t('home.newHere')}</span>`
              : html`<span class="pill">${t('home.tested', { n: m.savedActivityTasks ?? m.testedTasks ?? 0 })}${m.forgedHere ? `${t('common.dot')}⚒${num(m.forgedHere)}` : ''}</span>`}
            ${m.note ? html`<span class="pill" dir="auto">${m.note.slice(0, 30)}</span>` : nothing}
            ${m.sharedForSupport ? html`<span class="pill shared" title=${t('home.sharedTitle')}>${t('home.sharedPill')}</span>` : nothing}
          </div>
        </button>
        <button class="btn small moment-practice" @click=${() => go(practicePath(m.id))}>${t('moment.practice')}</button>
      </div>
    `;
  }

  /** Does this learner's pair have a starting check? (English has no bank.) */
  private hasStartingCheck(): boolean {
    const s = session();
    const target = this.health?.beta ? (s.status === 'known' ? s.target : currentTarget()) : this.health?.target;
    const pair = this.health?.pairs?.find((p) => p.target === target) ?? pairFor(target);
    return !!pair?.placement;
  }

  /**
   * The labelled way into onboarding (ticket v1-02): setting up for someone
   * who never has, the starting check for someone who set up without it.
   * Gone once there is a starting estimate; the ⋯ menu keeps /start reachable.
   */
  private renderSetupEntry() {
    if (!this.profile) return nothing;
    const learner = this.profile.learner;
    const check = this.hasStartingCheck();
    if (!learner) return html`
      <button class="btn ghost block setup-entry" style="margin-top:8px;text-transform:none" @click=${() => go('/start')}>${t('home.setupNew')}</button>
      ${check ? html`<button class="btn ghost block check-entry" style="margin-top:8px;text-transform:none" @click=${() => go('/start#check')}>${t('home.checkEntry')}</button>` : nothing}`;
    if (!learner.placement && check) return html`
      <button class="btn ghost block check-entry" style="margin-top:8px;text-transform:none" @click=${() => go('/start#check')}>${t('home.checkEntry')}</button>`;
    return nothing;
  }

  /**
   * The day's round under the town (v1 ticket 15): its cards and the one button that starts it, or continues it
   * once part of it is reached; "done" once nothing is left today. The town above shows what needs care (ivy,
   * lamps going out, the fog).
   */
  private renderRound() {
    const r = this.learn?.round;
    if (!r || (r.cards === 0 && r.done === 0)) return nothing;
    if (r.cards === 0) return html`<div class="round-bar"><div class="muted small round-done ${oneButtonOn() ? 'whisper' : ''}">${t('round.doneToday')}</div></div>`;
    const one = r.cards === 1;
    const label = r.done > 0
      ? (one ? t('round.continueOne') : t('round.continue', { n: r.cards }))
      : (one ? t('round.startOne') : t('round.start', { n: r.cards }));
    // One capture button: one small glowing pill floating over the lantern, in place of the count and the big button.
    if (oneButtonOn()) {
      return html`<div class="round-bar floating-round"><button class="review-entry round-pill" @click=${() => go('/round')}>${label}</button></div>`;
    }
    return html`
      <div class="round-bar">
        <span class="pill round-cards">${one ? t('round.cardsTodayOne') : t('round.cardsToday', { n: r.cards })}</span>
        <button class="btn primary block review-entry" @click=${() => go('/round')}>${label}</button>
      </div>`;
  }

  /** Why the scenario exam is not available yet: its server-side word boundary. */
  private renderExamLocked() {
    const l = this.learn;
    if (!l || l.examEligible || l.examToGo === undefined || l.examWordsNeeded === undefined) return nothing;
    const key = l.examsTaken > 0 ? 'home.examLockedNext' : 'home.examLocked';
    const recovery = l.examRecovery;
    const action = recovery && 'action' in recovery ? recovery.action : undefined;
    const href = action === 'round' ? '/round' : action === 'capture' ? '/record#wanted' : undefined;
    return html`<div class="muted small exam-locked" style="text-align:center;margin-top:8px">
      <div>${t(key, { needed: l.examWordsNeeded, n: l.examToGo })}</div>
      ${recovery ? html`<div>${t(`home.examRecovery.${recovery.reason}`)}</div>` : nothing}
      ${href && action ? html`<a class="btn ghost small" href=${href} @click=${(e: Event) => { e.preventDefault(); go(href); }}>${t(`home.examRecovery.action.${action}`)}</a>` : nothing}
    </div>`;
  }

  /**
   * The top bar over the town: the town's name as its crest ("Your town" unnamed), the moments, the legend and the ⋯
   * menu as pills, then one line of counts (the fog, the forged words, the lamps going out or lit), and a chip while
   * drafts need a look. Check-occasionally info and the learning setup live in the ⋯ menu (always shown, so /start
   * stays reachable).
   */
  private renderBar() {
    const own = this.own, name = own ? townPropsOf(own).name : undefined, built = !!own?.districts.length;
    const fog = this.learn?.fogCount ?? 0;
    const c = own?.counts;
    // A town with no upkeep to read (every word known, or some set aside): its own caption instead of lamps.
    const resting = own ? restingCaption(own.buildings) : null;
    const showSpend = !!this.usage && this.usage.totalUsd > 0;
    const setupLabel = this.profile?.learner ? t('home.setup') : t('home.setupNew');
    const offerCheck = !!this.profile?.learner && !this.profile.learner.placement && this.hasStartingCheck();
    const bands = this.profile?.learner?.placement?.bands;
    const placed = ['read', 'listen', 'retrieve'] as const;
    const v = this.drafts;
    const draftsWait = v.owner && (v.drafts.length > 0 || v.completed.length > 0 || !!v.expired);
    const unheard = v.owner && v.drafts.some(needsHearing);
    const identity = session();
    return html`
      <header class="home-bar">
        <h1 class="sr-only"><bdi>Eaves</bdi></h1>
        <div class="home-bar-in">
        <div class="home-row">
          <div class="home-crest ${built ? 'ht-title' : ''}">${name ? html`<bdi dir="auto">${name}</bdi>` : t('town.homeTitle')}</div>
          ${SHOW_CIRCLE && this.health?.beta
            ? html`<button class="home-pill home-circle" @click=${() => go('/circle')}>${t('home.circle')}${raidWaits(this.raids) ? html`<span class="home-mark"><span class="sr-only">${t('raid.waiting')}</span></span>` : nothing}</button>`
            : nothing}
          ${this.health?.beta ? html`<activity-indicator></activity-indicator>` : nothing}
          <button class="home-pill icon home-moments" aria-label=${t('home.moments')} title=${t('home.moments')} @click=${openMoments}>${LIST}</button>
          ${own
            ? html`<button class="home-pill icon home-legend" aria-label=${t('town.help')} title=${t('town.help')} @click=${this.openLegend}>?</button>`
            : nothing}
          <details class="overflow-menu">
            <summary aria-label=${t('home.appInfo')}>⋯</summary>
            <div class="overflow-pop">
              ${identity.status === 'known' ? html`<div class="account-language-pair" style="display:block">
                <strong>${t('account.pairTitle')}</strong>
                <p>${t('account.pair', { target: t(`lang.${identity.target}`), explain: t(`lang.${identity.explain}`), interface: t(`lang.${currentLang()}`) })}</p>
                <p class="muted small">${t('account.pairFixed')}</p>
              </div>` : nothing}
              ${this.profile ? html`<button class="menu-action setup-entry" @click=${() => go('/start')}>
                ${setupLabel}
              </button>` : nothing}
              ${offerCheck
                ? html`<button class="menu-action check-entry" @click=${() => go('/start#check')}>${t('home.checkEntry')}</button>`
                : nothing}
              ${bands
                ? html`<div class="estimate"><span>${t('home.estimate')}</span><strong style="text-align:end">${placed.every((k) => bands[k] === 'not_enough')
                    ? t('band.not_enough')
                    : placed.map((k, n) => html`${n ? html`<br />` : nothing}${t(`skill.${k}`)}: ${t(`band.${bands[k]}`)}`)}</strong></div>`
                : nothing}
              ${this.health?.version ? html`<div><span>${t('home.version')}</span><strong><bdi dir="ltr">v${this.health.version}</bdi></strong></div>` : nothing}
              ${this.health?.mock ? html`<div><span>${t('home.mode')}</span><strong>${t('home.modeMock')}</strong></div>` : nothing}
              ${showSpend ? html`<div><span>${t('home.spent')}</span><strong><bdi dir="ltr">$${num(this.usage!.totalUsd.toFixed(2))}</bdi></strong></div>` : nothing}
              <button class="menu-action watch-opening" @click=${this.watchOpening}>${t('home.watchOpening')}</button>
              ${this.own
                ? html`<button class="menu-action name-town" @click=${() => this.openAsk('menu')}>${this.own.name ? t('home.renameTown') : t('map.nameTitle')}</button>`
                : nothing}
              <button class="menu-action feel-switch" role="switch" aria-checked=${this.feel ? 'true' : 'false'} @click=${this.toggleFeel}>
                ${t('feel.switch')}: <span class="feel-state">${t(this.feel ? 'feel.on' : 'feel.off')}</span>
              </button>
              <button class="menu-action music-switch" role="switch" aria-checked=${this.music ? 'true' : 'false'} @click=${this.toggleMusic}>
                ${t('music.switch')}: <span class="feel-state">${t(this.music ? 'feel.on' : 'feel.off')}</span>
              </button>
              <button class="menu-action diag-switch" role="switch" aria-checked=${this.diag ? 'true' : 'false'} @click=${this.toggleDiag}>
                ${t('diag.switch')}: <span class="feel-state">${t(this.diag ? 'feel.on' : 'feel.off')}</span>
              </button>
              <div class="research-settings"></div>
              <div class="replay-settings"></div>
              <button class="menu-action reminders-entry" @click=${() => { this.closeMenu(); openReminders(); }}>${t('reminder.menu')}</button>
              <button class="menu-action share-entry" @click=${() => { this.closeMenu(); openCapture('shared'); }}>${t('shared.menu')}</button>
              <button class="menu-action report-entry" @click=${() => { this.closeMenu(); openReport(); }}>${t('report.menu')}</button>
              ${this.owner
                ? html`<button class="menu-action invite-friend" @click=${() => { this.closeMenu(); this.inviting = true; }}>${t('invite.menu')}</button>`
                : nothing}
              ${this.health?.beta
                ? html`<button class="menu-action" @click=${this.logout}>${t('home.logout')}</button>`
                : nothing}
            </div>
          </details>
        </div>
        <div class="home-counts" aria-label=${t('home.progressAria')}>
          ${fog > 0
            ? html`<button class="pill fog-count" aria-label=${t('fog.waitingTitle')} title=${t('fog.waitingTitle')} @click=${this.openLegend}>${fog === 1 ? t('fog.waitingOne') : t('fog.waiting', { n: fog })}</button>`
            : nothing}
          ${this.renderForged()}
          ${c && built
            ? resting
              ? html`<button class="home-caption" data-state=${resting === 'town.knownOnly' ? 'known' : 'resting'} aria-label=${t(resting)} title=${t(resting)} @click=${this.openLegend}>${t(resting)}</button>`
              : c.fading
                ? this.own?.buildings.some(b => b.condition)
                  ? html`<button class="home-caption ht-fading" aria-label=${t(c.fading === 1 ? 'town.maintenanceFadingOne' : 'town.maintenanceFading', { n: c.fading })} title=${t(c.fading === 1 ? 'town.maintenanceFadingOne' : 'town.maintenanceFading', { n: c.fading })} @click=${this.openLegend}>${c.fading === 1 ? t('town.maintenanceFadingOne') : t('town.maintenanceFading', { n: c.fading })}</button>`
                  : html`<button class="home-caption ht-fading" aria-label=${t('round.fadingTitle')} title=${t('round.fadingTitle')} @click=${this.openLegend}>${c.fading === 1 ? t('round.fadingOne') : t('round.fading', { n: c.fading })}</button>`
                : html`<button class="home-caption" aria-label=${t(this.lightsKey(), { lamps: c.lamps, ivy: c.ivy })} @click=${this.openLegend}>${t(this.lightsKey(), { lamps: c.lamps, ivy: c.ivy })}</button>`
            : nothing}
        </div>
        ${unheard ? html`<button class="home-pill drafts-chip drafts-resume-recording" @click=${this.resumeRecording}>${t('drafts.resumeRecording')}</button>` : nothing}
        ${draftsWait ? html`<button class="home-pill drafts-chip" @click=${openMoments}>${t('home.draftsChip')}</button>` : nothing}
        </div>
      </header>
    `;
  }

  /**
   * Forged words (v1 ticket 10): box 3 and up, the town's stone and gold, counted short in the top bar, with the one
   * plain line on what forged means as its title and in the legend. Shown once the learner has any words.
   */
  private renderForged() {
    const n = this.forged();
    if (n === null) return nothing;
    return html`<button class="pill forged-count" data-forged=${n} aria-label=${this.forgedLine(n)} title=${this.forgedLine(n)} @click=${this.openLegend}>${n === 1 ? t('home.forgedShortOne') : t('home.forgedShort', { n })}</button>`;
  }

  private forged(): number | null {
    const l = this.learn;
    return !l || l.totalItems <= 0 ? null : l.forgedCount;
  }

  private forgedLine(n: number) {
    return n === 0 ? t('home.forgedNone') : n === 1 ? t('home.forgedOne') : t('home.forged', { n });
  }

  /** The town's lamps line: a town with no upkeep to read gets its own line; otherwise the maintenance line once a building has a condition. */
  private lightsKey(): string {
    const resting = this.own ? restingCaption(this.own.buildings) : null;
    if (resting) return resting;
    return this.own?.buildings.some(b => b.condition) ? 'town.maintenanceLights' : 'town.homeLights';
  }

  /** "What am I seeing?": the legend (materials, lamps, the moon and the day, lanes, forged) and the word lookups' credit, over the town. */
  private renderLegend() {
    const own = this.own;
    if (!this.legendOpen || !own) return nothing;
    const c = own.counts, built = own.districts.length > 0, n = this.forged();
    return html`
      <div class="moments-dim" aria-hidden="true" @click=${this.closeLegend}></div>
      <section class="moments-panel home-legend-sheet" role="dialog" aria-modal="true" aria-labelledby="legend-title">
        <div class="moments-head">
          <h2 id="legend-title">${t('town.help')}</h2>
          <button class="btn ghost icon legend-close" @click=${this.closeLegend} aria-label=${t('tend.close')}>✕</button>
        </div>
        ${built
          ? html`<p class="ht-materials">${t('town.homeMaterials', { fog: c.fog, scaffolding: c.scaffolding, wood: c.wood, stone: c.stone, gold: c.gold })}</p>
            <p class="ht-lights">${t(this.lightsKey(), { lamps: c.lamps, ivy: c.ivy })}</p>
            ${n !== null ? html`<p class="forged-line">${this.forgedLine(n)}</p>` : nothing}
            <p class="muted">${t('town.homeHint')}</p>
            <p class="muted">${t(own.buildings.some(b => b.condition) ? 'town.maintenanceExplain' : 'town.homeExplain')}</p>
            ${!own.day && c.scaffolding + c.wood + c.stone + c.gold > 0 ? html`<p class="muted sky-line">${t('townLegend.moon')}</p>` : nothing}
            ${own.day ? html`<p class="muted sky-line">${t('townLegend.day', { min: DAY_MIN })}</p>` : nothing}
            <p class="muted">${t('town.homeLanes')}</p>`
          : html`<p class="muted empty-town-help">${t('land.legendEmpty')}</p>
            ${own.stone && !own.name ? html`<p class="muted deferred-name-help">${t('land.namingDeferred')}</p>` : nothing}`}
        ${hasCredit(own) ? html`<p class="ht-credit">${t('town.credit')}
          <bdi><a href="https://en.wiktionary.org/" target="_blank" rel="noopener">Wiktionary</a></bdi>
          (<bdi><a href="https://kaikki.org/" target="_blank" rel="noopener">kaikki.org</a></bdi>)${t('common.dot')}
          <bdi><a href="https://creativecommons.org/licenses/by-sa/4.0/" target="_blank" rel="noopener">CC BY-SA 4.0</a></bdi></p>` : nothing}
        <p class="music-credit">${t('music.credit')}
          <bdi><a href="https://member.keymusician.com/Member/FluidR3_GM/index.html" target="_blank" rel="noopener">FluidR3 GM</a></bdi>
          (<bdi>Frank Wen</bdi>)${t('common.dot')}
          <bdi><a href="https://creativecommons.org/licenses/by/3.0/" target="_blank" rel="noopener">CC BY 3.0</a></bdi></p>
      </section>`;
  }

  private legendFrom: HTMLElement | null = null;
  private openLegend = (e: Event) => {
    this.closeMenu();
    this.legendFrom = e.currentTarget as HTMLElement;
    openHash(LEGEND, 'legend');
    this.legendOpen = true;
    void this.updateComplete.then(() => this.querySelector<HTMLElement>('.legend-close')?.focus());
  };

  /** Its ✕, Escape or the dim: the entry goes (Back does the same through `onLegendPop`). */
  private closeLegend = () => {
    this.legendShut();
    closeHash('legend');
  };

  private onLegendPop = () => {
    if (hashOpen(LEGEND)) this.legendOpen = true;
    else if (this.legendOpen) this.legendShut();
  };

  private legendShut() {
    this.legendOpen = false;
    const back = this.legendFrom;
    this.legendFrom = null;
    void this.updateComplete.then(() => focusBack(back));
  }

  private onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && this.legendOpen && !document.documentElement.classList.contains('capture-open')) this.closeLegend();
  };

  /** The first night's layers over home: the naming sheet, or the opening watched again. */
  private renderFirstNight() {
    const target = this.own?.language ?? currentTarget();
    return html`
      ${this.asking
        ? html`<eaves-map .mode=${'ask'} .target=${target} .explain=${currentLang()} .check=${normaliseTownName}
            .lay=${(name: string | null) => api.townName(name)} .timeZone=${tzOf(this.allowance)} @map-done=${this.onMapDone}></eaves-map>`
        : nothing}
      ${this.replaying
        ? html`<eaves-opening .target=${target} .explain=${currentLang()} .replay=${true}
            .townName=${this.own?.name ?? null} .foundedAt=${this.own?.foundedAt ?? null}
            @opening-end=${this.onReplayEnd}></eaves-opening>`
        : nothing}`;
  }

  /** The panel over the town's foot: the empty land's words, the day's round, Record and Type, and one short line. */
  private renderDock() {
    if (oneButtonOn()) return this.renderFloatingDock();
    const own = this.own, empty = !!own && !own.districts.length, name = own ? townPropsOf(own).name : undefined;
    const a = this.allowance, ai = aiOf(a), gate = gateOf(a);
    const gateInOffer = !!own?.newcomers && !!gate;
    return html`
      <div class="home-dock">
        ${own && !empty ? html`<div class="home-hint">${t('home.tapHint')}</div>` : nothing}
        <div class="home-panel">
          ${empty
            ? html`<h2 class="ht-title ht-land">${name ? tPart('land.of', 'name', html`<bdi dir="auto">${name}</bdi>`) : t('town.homeTitle')}</h2>
              <p class="ht-land-note">${t('land.empty')}</p>
              ${own?.stone && !own.name ? html`<p class="ht-land-note deferred-name-note">${t('land.namingDeferred')}</p>` : nothing}`
            : nothing}
          ${this.renderRound()}
          ${own?.newcomers || own?.newcomersFailed || own?.newcomerReadiness
            ? html`<newcomers-offer .offer=${own.newcomers ?? null} .readiness=${own.newcomerReadiness} ?failed=${!!own.newcomersFailed} .townName=${name} .gate=${gate} .timezone=${tzOf(a)} @newcomers-gated=${this.onNewcomersGated} @newcomers-hidden=${this.onNewcomersHidden} @newcomers-town-read=${this.onTownRead}></newcomers-offer>`
            : nothing}
          ${raidWaits(this.raids) ? html`<raid-gate .overview=${this.raids}></raid-gate>` : nothing}
          ${this.requests.length ? html`<friend-request-gate .requests=${this.requests}></friend-request-gate>` : nothing}
          ${renderHomeWays(openCapture)}
          ${(a && a.limit !== null) || ai || gate
            ? html`<div class="home-notes muted small">
                ${a && a.limit !== null
                  ? html`<span class="allowance-note">${a.remaining > 0 ? t('home.allowanceLeft', { remaining: a.remaining, limit: a.limit ?? 0 }) : t('home.allowanceNone')}</span>`
                  : nothing}
                ${ai ? html`<span class="ai-note">${aiOut(a) ? aiReason(ai) : aiLine(ai)}</span>` : nothing}
        ${ai ? html`<span class="ai-scope">${t('ai.scope')}</span>` : nothing}
        ${this.allocationChange ? html`<span class="ai-allocation-change" role="status">${this.allocationChange}</span>` : nothing}
                ${gateInOffer ? renderGateCounts(gate, { dueCount: a?.limit === null ? undefined : a?.dueCount, roundCards: a?.limit === null ? undefined : a?.round?.cards }) : renderGate(gate, { dueCount: a?.limit === null ? undefined : a?.dueCount, roundCards: a?.limit === null ? undefined : a?.round?.cards, timezone: tzOf(a) })}
              </div>`
            : nothing}
        </div>
      </div>`;
  }

  /**
   * With one capture button (ticket 44), the dock floats over the town with no box: a soft scrim at the screen's foot,
   * the lantern at its centre, the day's round as one glowing pill above it, the tap hint as a whisper that fades, and
   * whatever else appears (the empty land's note, newcomers, the notes) blooming in as glass bubbles. The notes fold
   * into one chip ("2 left today") unless they block or warn: no moments left today, the AI allowance spent or nearly,
   * or the review gate holding new moments; then they show in full. Empty space around the pieces lets the town pan.
   */
  private renderFloatingDock() {
    const own = this.own, empty = !!own && !own.districts.length, name = own ? townPropsOf(own).name : undefined;
    const a = this.allowance, ai = aiOf(a), gate = gateOf(a);
    // The newcomers' offer shows the requirement itself (it holds Meet them), so the notes leave it out then.
    const gateInOffer = !!own?.newcomers && !!gate;
    const limited = !!a && a.limit !== null;
    const left = a && a.limit !== null ? a.remaining : 0;
    // Low: less left than one recorded moment reserves ($0.02), or under a tenth of the month's share.
    const aiLow = !!ai && !ai.exhausted && ai.remainingUsd < Math.max(0.02, ai.allocationUsd * 0.1);
    const urgent = !!this.allocationChange || (limited && left <= 0) || aiOut(a) || aiLow || (gate?.status === 'pending' && !gateInOffer);
    const notes = limited || ai || gate
      ? html`
        ${a && a.limit !== null
          ? html`<span class="allowance-note">${a.remaining > 0 ? t('home.allowanceLeft', { remaining: a.remaining, limit: a.limit ?? 0 }) : t('home.allowanceNone')}</span>`
          : nothing}
        ${ai ? html`<span class="ai-note">${aiOut(a) ? aiReason(ai) : aiLine(ai)}</span>` : nothing}
        ${ai ? html`<span class="ai-scope">${t('ai.scope')}</span>` : nothing}
        ${this.allocationChange ? html`<span class="ai-allocation-change" role="status">${this.allocationChange}</span>` : nothing}
        ${gateInOffer ? renderGateCounts(gate, { dueCount: a?.limit === null ? undefined : a?.dueCount, roundCards: a?.limit === null ? undefined : a?.round?.cards }) : renderGate(gate, { dueCount: a?.limit === null ? undefined : a?.dueCount, roundCards: a?.limit === null ? undefined : a?.round?.cards, timezone: tzOf(a) })}`
      : null;
    return html`
      <div class="home-dock floating">
        <div class="dock-scrim" aria-hidden="true"></div>
        ${own && !empty ? html`<div class="home-hint whisper">${t('home.tapHint')}</div>` : nothing}
        <div class="home-panel">
          ${empty
            ? html`<h2 class="ht-title ht-land">${name ? tPart('land.of', 'name', html`<bdi dir="auto">${name}</bdi>`) : t('town.homeTitle')}</h2>
              <p class="ht-land-note glass">${t('land.empty')}</p>
              ${own?.stone && !own.name ? html`<p class="ht-land-note glass deferred-name-note">${t('land.namingDeferred')}</p>` : nothing}`
            : nothing}
          ${notes && urgent ? html`<div class="home-notes dock-notes-full glass muted small" role="status">${notes}</div>` : nothing}
          ${own?.newcomers || own?.newcomersFailed || own?.newcomerReadiness
            ? html`<newcomers-offer .offer=${own.newcomers ?? null} .readiness=${own.newcomerReadiness} ?failed=${!!own.newcomersFailed} .townName=${name} .gate=${gate} .timezone=${tzOf(a)} @newcomers-gated=${this.onNewcomersGated} @newcomers-hidden=${this.onNewcomersHidden} @newcomers-town-read=${this.onTownRead}></newcomers-offer>`
            : nothing}
          ${raidWaits(this.raids) ? html`<raid-gate .overview=${this.raids}></raid-gate>` : nothing}
          ${this.requests.length ? html`<friend-request-gate .requests=${this.requests}></friend-request-gate>` : nothing}
          ${this.renderRound()}
          ${renderHomeWays(openCapture)}
          ${notes && !urgent
            ? html`<div class="dock-notes ${this.notesOpen ? 'open' : ''}">
                <button class="notes-chip" aria-expanded=${this.notesOpen ? 'true' : 'false'} aria-controls="dock-notes-body" @click=${this.toggleNotes}>
                  ${left === 1 ? t('dock.leftOne') : t('dock.left', { n: left })}
                </button>
                <div class="notes-body" id="dock-notes-body" ?inert=${!this.notesOpen}>
                  <div class="notes-in home-notes muted small">${notes}</div>
                </div>
              </div>`
            : nothing}
        </div>
      </div>`;
  }

  /** The notes chip opens in place; a tap on it again, or anywhere else, folds it. */
  private toggleNotes = () => {
    this.notesOpen = !this.notesOpen;
    if (this.notesOpen) {
      document.addEventListener('pointerdown', this.onNotesOutside, true);
      document.addEventListener('keydown', this.onNotesKey, true);
    } else this.closeNotesListeners();
  };

  private closeNotesListeners() {
    document.removeEventListener('pointerdown', this.onNotesOutside, true);
    document.removeEventListener('keydown', this.onNotesKey, true);
  }

  private onNotesKey = (e: KeyboardEvent) => {
    if (e.key !== 'Escape') return;
    this.notesOpen = false;
    this.closeNotesListeners();
    this.querySelector<HTMLElement>('.notes-chip')?.focus();
  };

  private onNotesOutside = (e: Event) => {
    if ((e.target as Element | null)?.closest?.('.dock-notes')) return;
    this.notesOpen = false;
    this.closeNotesListeners();
  };

  /** The moments sheet's body: drafts, the setup and exam entries, then search, tabs and the lists. */
  private renderMoments() {
    const hasMore = this.moments.length < this.total;
    const fresh = this.moments.filter((m) => this.isNew(m));
    const done = this.moments.filter((m) => !this.isNew(m));
    const allCount = this.newCount + this.practicedCount;
    const showActive = !!this.learn && this.learn.activeItems !== undefined && this.learn.activeItems !== this.learn.totalItems;
    return html`
      <div class="moments-create" role="group" aria-label=${t('home.createMoment')}>
        <button class="btn moments-create-record" @click=${() => openCapture('record')}>${t('ways.record')}</button>
        <button class="btn moments-create-type" @click=${() => openCapture('wanted')}>${t('ways.type')}</button>
      </div>
      ${this.renderDraftsNotice()}
      ${this.renderSetupEntry()}
      ${this.learn && this.learn.examEligible
        ? html`<button class="btn block exam-entry" style="margin-top:8px" ?disabled=${aiOut(this.allowance)} @click=${() => go('/exam')}>
            ${t('home.exam', { n: this.learn!.examWords ?? 0 })}
          </button>
          ${aiOut(this.allowance)
            ? html`<div class="muted small ai-reason" style="text-align:center;margin-top:4px">
                ${t('ai.examReason', { reason: aiReason(aiOf(this.allowance)!) })}
              </div>`
            : nothing}`
        : this.renderExamLocked()}
      ${showActive
        ? html`<div style="margin-top:8px"><span class="pill" title=${t('home.activeTitle')}>${t('home.active', { n: this.learn!.activeItems ?? 0 })}</span></div>`
        : nothing}
      ${this.loading
        ? html`<div class="empty"><span class="spinner"></span></div>`
        : this.total === 0 && !this.query
          ? html`<div class="empty"><div class="big">🎧</div><div>${t('home.noMoments')}</div></div>`
          : html`
            <div style="display:flex;gap:8px;align-items:center;margin:12px 0 8px">
              <input
                type="search"
                placeholder=${t('home.search')}
                .value=${this.query}
                @input=${this.onQuery}
                aria-label=${t('home.searchAria')}
                style="flex:1;min-height:40px;border-radius:10px;border:1px solid var(--line);background:var(--bg-elev);color:inherit;padding:0 12px"
              />
              ${this.total > 0 ? html`<span class="muted small">${num(this.moments.length)}/${num(this.total)}</span>` : nothing}
            </div>
            ${allCount > 0 || this.query
              ? html`<div class="tabs" role="tablist" aria-label=${t('home.filterAria')}>
                  <button class="btn small ${this.filter === 'all' ? 'primary' : ''}" role="tab" aria-selected=${this.filter === 'all'}
                    style="flex:1;min-height:38px;text-transform:none" @click=${() => this.setFilter('all')}>${t('home.tabAll', { n: allCount })}</button>
                  <button class="btn small ${this.filter === 'new' ? 'primary' : ''}" role="tab" aria-selected=${this.filter === 'new'}
                    style="flex:1;min-height:38px;text-transform:none" @click=${() => this.setFilter('new')}>${t('home.tabNew', { n: this.newCount })}</button>
                  <button class="btn small ${this.filter === 'practiced' ? 'primary' : ''}" role="tab" aria-selected=${this.filter === 'practiced'}
                    style="flex:1;min-height:38px;text-transform:none" @click=${() => this.setFilter('practiced')}>${t('home.tabPracticed', { n: this.practicedCount })}</button>
                </div>`
              : nothing}
            ${this.moments.length === 0
              ? this.filter === 'new'
                ? html`<div class="empty"><div class="big">✓</div><div>${t('home.allCaught')}</div><div class="small">${t('home.allCaughtHint')}</div></div>`
                : this.filter === 'practiced'
                  ? html`<div class="empty"><div>${t('home.noPracticed')}</div><div class="small">${t('home.noPracticedHint')}</div></div>`
                  : html`<div class="empty"><div>${t('home.noMatch', { q: this.query })}</div></div>`
              : html`
                ${(this.filter === 'all' || this.filter === 'new') && fresh.length
                  ? html`<div class="section-head"><span>${t('home.sectionNew')}</span><span class="pill accent">${num(this.filter === 'new' ? this.newCount : fresh.length)}</span></div>
                    <div class="moment-list fresh">${repeat(fresh, (m) => m.id, (m) => this.renderMomentItem(m, true))}</div>`
                  : nothing}
                ${(this.filter === 'all' || this.filter === 'practiced') && done.length
                  ? html`<div class="section-head"><span>${t('home.sectionPracticed')}</span><span class="pill">${this.filter === 'practiced' ? num(this.practicedCount) : `${num(done.length)}${hasMore ? '+' : ''}`}</span></div>
                    <div class="moment-list">${repeat(done, (m) => m.id, (m) => this.renderMomentItem(m, false))}</div>`
                  : nothing}
              `}
            ${hasMore
              ? html`
                <div id="list-sentinel" style="min-height:8px"></div>
                <button class="btn block" style="margin-top:8px" ?disabled=${this.loadingMore} @click=${() => this.loadPage(false)}>
                  ${this.loadingMore ? html`<span class="spinner"></span>` : t('home.showMore', { n: this.total - this.moments.length })}
                </button>`
              : nothing}
          `}`;
  }

  /**
   * Home is the town, full screen (the arrival prototype D's idle town, in the first night's D look): the town under a
   * top bar and a floating panel, with the moments, the legend, the invite sheet and the capture panel over it.
   */
  render() {
    const own = this.own;
    // Neither the town nor a moment: the old empty state, once the town read has settled (so it never flashes).
    const noTown = this.townSettled && !own && !this.loading && this.total === 0 && !this.query && !this.error;
    const waiting = !own && (!this.townSettled || this.loading);
    return html`
      <div class="home">
        <div class="home-night" aria-hidden="true"></div>
        ${this.townReady && own ? html`<home-town .given=${own} ?hold=${!!this.momentId || routeHere()?.tab === 'shared'} @town-shown=${this.onTownShown} @town-read=${this.onTownRead}></home-town>` : nothing}
        ${waiting ? html`<div class="home-wait"><span class="spinner"></span></div>` : nothing}
        ${this.renderBar()}
        ${this.error && this.moments.length === 0 && !this.loading
          ? html`<div class="card home-card"><strong>${t('home.cantReach')}</strong><div class="muted small">${this.error}</div></div>`
          : noTown
            ? html`<div class="card home-card empty"><div class="big">🎧</div><div>${t('home.noMoments')}</div></div>`
            : nothing}
        ${this.leaving || this.inviting
          ? html`<div class="home-over">
              ${this.renderLogoutChoice()}
              ${this.inviting ? html`<invite-sheet @invite-close=${() => { this.inviting = false; }}></invite-sheet>` : nothing}
            </div>`
          : nothing}
        ${this.renderDock()}
        ${this.renderLegend()}
      </div>
      <moments-sheet .body=${this.renderMoments()}></moments-sheet>
      <capture-sheet @capture-close=${this.onCaptureClose}></capture-sheet>
      ${this.panelId
        ? keyed(this.panelId, html`<moment-view class=${this.panelLeaving ? 'leaving' : ''} .momentId=${this.panelId}
            @moment-close=${this.closeMoment} @moment-changed=${this.onMomentChanged} @moment-deleted=${this.onMomentDeleted}></moment-view>`)
        : nothing}
      ${this.renderFirstNight()}
    `;
  }

  /**
   * `/moment/<id>` came or went. Opening one from home (its list, a town sheet) is a history entry on top of home, so
   * its ways out go back; a deep link or another screen's link replaces it with home instead. Going, the panel slides
   * down (fades under reduced motion) before it leaves the page, and the camera flies back to the idle town.
   */
  protected willUpdate(changed: Map<PropertyKey, unknown>) {
    if (!changed.has('momentId')) return;
    const was = changed.get('momentId') as string | undefined;
    window.clearTimeout(this.leaveTimer);
    if (this.momentId) {
      this.panelPushed = was === '' && this.hasUpdated;
      this.panelId = this.momentId;
      this.panelLeaving = false;
      return;
    }
    if (!this.panelId) return;
    this.panelLeaving = true;
    this.leaveTimer = window.setTimeout(() => { this.panelId = ''; this.panelLeaving = false; }, 240);
    const town = this.querySelector<HomeTown>('home-town');
    this.focusedId = '';
    if (this.namingDue && this.townSettled) window.setTimeout(this.onTownShown, 300);
    town?.focusMoment?.(null);
    if (this.panelChanged) {
      this.panelChanged = false;
      void town?.refresh?.();
      void this.loadPage(true);
    }
  }

  /** The panel's close, Back and Escape (moment-view): back to the town. */
  private closeMoment = () => {
    if (!this.momentId) return;
    if (this.panelPushed && history.length > 1) history.back();
    else go('/', true);
  };

  private onMomentChanged = () => { this.panelChanged = true; };
  private onMomentDeleted = () => { this.panelChanged = true; this.panelPushed = false; };

  /** The panel's district is framed above it: the panel rests at `REST` of the screen, under the top bar. */
  private focusPanel() {
    if (!this.momentId) return;
    const town = this.querySelector<HomeTown>('home-town');
    const bar = this.querySelector('.home-bar');
    if (!town?.focusMoment) return;
    const top = bar ? bar.getBoundingClientRect().bottom : 56;
    town.focusMoment({ id: this.momentId, top, bottom: innerHeight * (1 - REST) });
    this.focusedId = this.momentId;
    this.focusedTown = town;
  }
  private focusedId = '';
  private focusedTown: Element | null = null;

  /**
   * Meet them was refused by today's review requirement, which it fixed: the batch is now a card the round serves,
   * so the round's pill and the allowance are read again (or "Today's round is done" would stand under Review now).
   */
  private onNewcomersGated = () => {
    void this.loadAllowance();
    void api.learnStatus().then((l) => (this.learn = l)).catch(() => {});
  };

  /** The offer no longer shows (Not now, gone, failed): the town drops it, so the notes carry the gate again. */
  private onNewcomersHidden = (e: Event) => {
    if (!this.own?.newcomers) return;
    const { newcomers: _gone, ...own } = this.own;
    this.own = (e as CustomEvent<{ failed: boolean }>).detail?.failed ? { ...own, newcomersFailed: true } : own;
  };

  /** Home's town was read again (after tending in place): the counts follow it. */
  private onTownRead = (e: Event) => {
    this.own = (e as CustomEvent<OwnTown>).detail;
    void api.learnStatus().then((l) => (this.learn = l)).catch(() => {});
  };
}

function fmtDate(iso: string) {
  const d = new Date(iso);
  // Dates follow the interface language, so a Persian screen reads as Persian.
  return d.toLocaleString(currentLang(), { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}
